import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useResource from 'Hooks/useResource';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { MessageTypeOverrides } from 'Overrides/MessageTypeOverrides';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { fullDateAtTime } from 'Consts/DateFormats';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  FDAlert, FDBodyText, FDDialogContent, FDPrimaryActionButton, FDStack, FDTitleText,
} from '@facet-wealth/design';

const EmailTypePickerDialogContent = ({
  meetingResource,
  meetingAgendaResource,
  onSubmit,
  closeModal,
  onScheduleSendEmail,
  sendType,
  openModal,
}) => {
  const {
    resource: meetingResourceCopy,
    setAttribute: setMeetingResourceAttribute,
  } = useResource(meetingResource.copy());

  const {
    resource: meetingAgendaResourceCopy,
    setAttribute: setMeetingAgendaResourceAttribute,
  } = useResource(meetingAgendaResource.copy());

  const resourceWithOverrides = meetingAgendaResourceCopy.overrideMeta(MessageTypeOverrides(meetingResourceCopy));
  const { logEvent } = useEventTrackingLogEvent();
  const [disabled, setDisabled] = useState(false);

  const { householdResource } = useSimpleHousehold();
  const emails = [];
  if (householdResource != null) {
    householdResource.attr('clients').resources().list().forEach(clientResource => {
      clientResource.attr('emailAddresses').resources().list().forEach(emailResource => {
        if (emailResource.attr('isPreferred') === 'true') {
          emails.push(<li>{emailResource.attr('address')}</li>);
        }
      });
    });
  }

  return (
    <FDDialogContent>
      <FDStack direction={'column'} spacing={4} paddingY={2}>
        <FDAlert details={emails} title={'This will send an email to all preferred contacts in the household.'} />
        <FDStack>
          {sendType === 'scheduled'
            ? (
              <FDStack direction={'column'}>
                <FDBodyText value={'This email will be sent on:'} color={'textSecondary'} size={'sm'} />
                <FDTitleText value={moment(meetingAgendaResourceCopy.attr('scheduledSendCustomDate')).format(fullDateAtTime)} color={'textSecondary'} size={'lg'} />
              </FDStack>)
            : <FDBodyText value={'This email will be sent immediately'} color={'textSecondary'} size={'sm'} />
            }
        </FDStack>
        <FDStack direction={'column'}>
          <FDBodyText value={'This email is associated with a meeting on:'} color={'textSecondary'} size={'sm'} />
          <FDTitleText value={moment(meetingResourceCopy.attr('startTime')).format(fullDateAtTime)} color={'textSecondary'} size={'lg'} />
        </FDStack>
        <FDStack direction={'column'}>
          <FDBodyText value={'What type of email are you sending?'} color={'textSecondary'} size={'sm'} />
          <MetaControl
            resource={resourceWithOverrides}
            attrName="messageType"
            onChange={(value) => {
              logEvent('Selected Email Message Type', { value });
              setMeetingResourceAttribute('messageType', value);
              setMeetingAgendaResourceAttribute('messageType', value);
            }}
          />
        </FDStack>
        <FDPrimaryActionButton
          onClick={async () => {
            // copy the values from the resource copies to the actual resources for submitting
            meetingAgendaResource.attr('personalizedMessage', meetingAgendaResourceCopy.attr('personalizedMessage'));
            meetingAgendaResource.attr('messageType', meetingAgendaResourceCopy.attr('messageType'));
            meetingResource.attr('messageType', meetingResourceCopy.attr('messageType'));

            setDisabled(true); // Prevent double-clicking the submit button
            closeModal();
            if (sendType === 'scheduled') {
              logEvent('Scheduled Send: Scheduling meeting agenda', {
                householdId: meetingAgendaResourceCopy.attr('householdId'),
                meetingDate: moment(meetingResourceCopy.attr('startTime')).format(fullDateAtTime),
                scheduledDate: moment(meetingAgendaResourceCopy.attr('scheduledSendCustomDate')).format(fullDateAtTime),
                daysUntilMeeting: moment(meetingResourceCopy.attr('startTime')).diff(moment(), 'days'),
                messageType: meetingAgendaResourceCopy.attr('messageType'),
              });
              try {
                await onScheduleSendEmail();
              } catch (e) {
                openModal();
                setDisabled(false);
              }
            } else {
              try {
                await onSubmit();
              } catch (e) {
                openModal();
                setDisabled(false);
              }
            }
          }}
          disabled={disabled}
          test="email-send-button"
          label={sendType === 'scheduled' ? 'Schedule' : 'Send Email'}
        />
      </FDStack>
    </FDDialogContent>

  );
};

EmailTypePickerDialogContent.defaultProps = {
};

EmailTypePickerDialogContent.propTypes = {
  meetingResource: PropTypes.object.isRequired,
  meetingAgendaResource: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onScheduleSendEmail: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  sendType: PropTypes.string.isRequired,
};

export default EmailTypePickerDialogContent;
